import styles from './Notifications.module.css';

import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import ExpandMore from '@mui/icons-material/ExpandMore';
import Divider from '@mui/material/Divider';

import { useEffect, useCallback } from 'react';

export const Notifications = ({ user, handleNotificationChange }) => {
    useEffect(() => {
        if (!user.notifications?.email?.shiftCancelled && !user.notifications?.sms?.shiftCancelled && !user.notifications?.voice?.shiftCancelled) {
            // Ensure at least one method is enabled by default (default to email)
            handleNotificationChange({ target: { name: 'email.shiftCancelled', checked: true } });
        }

    }, [user.notifications, handleNotificationChange]);

    const handleCancelledShiftChange = useCallback((e) => {
        const { name, checked } = e.target;

        if (!checked) {
            // Ensure at least one method remains enabled
            const emailEnabled = name === 'email.shiftCancelled' ? false : user.notifications?.email?.shiftCancelled;
            const smsEnabled = name === 'sms.shiftCancelled' ? false : user.notifications?.sms?.shiftCancelled;
            const voiceEnabled = name === 'voice.shiftCancelled' ? false : user.notifications?.voice?.shiftCancelled;

            if (!emailEnabled && !smsEnabled && !voiceEnabled) {
                return;
            }
        }

        handleNotificationChange(e);
    }, [user.notifications, handleNotificationChange]);

    return (
        <Accordion defaultExpanded={ false } sx={ { boxShadow: 'none', border: '1px solid', borderRadius: '8px', borderColor: 'divider' } }>
            <AccordionSummary expandIcon={ <ExpandMore /> } sx={ { borderBottom: '1px solid', borderColor: 'divider' } }>
                <Typography variant="h6">Notification Settings</Typography>
            </AccordionSummary>

            <AccordionDetails>
                <Typography sx={ { pb: 2 } }>Select your preferred notification method(s) for each type of notification.</Typography>

                <Alert variant="filled" severity="success" sx={ { mb: 2 } } onClose={ () => { } }>
                    PUSH notifications allow for native desktop and mobile notifications. They are more convenient than email or SMS since they utilize native device capabilities but are not as reliable as the other methods.
                </Alert>

                <Alert variant="filled" severity="info" sx={ { mb: 2 } } onClose={ () => { } }>
                    To receive PUSH notifications on your mobile device, you must add/install the app to the home screen and allow notifications when prompted.
                </Alert>

                <Alert variant="filled" severity="error" sx={ { mb: 2 } }>
                    PUSH notifications cannot be relied upon for critical or urgent communications. Always ensure at least one other method (email, SMS, or voice) is selected if you need reliable notifications.
                </Alert>

                <Divider sx={ { my: 2 } } />

                <Stack spacing={ 2 } sx={ { mb: 2 } }>
                    {/* MARK: New Event */ }
                    <Card variant='outlined'>
                        <CardHeader
                            title="New Event"
                            subheader="Immediate notification when a new event is created."
                        />

                        <CardContent>
                            <CardActions sx={ { p: 0 } }>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={ user.notifications?.email?.newEvent || false }
                                            onChange={ handleNotificationChange }
                                            name="email.newEvent"
                                            disabled={ !user.email }
                                        />
                                    }
                                    label="Email"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="push.newEvent"
                                            checked={ user.notifications?.push?.newEvent || false }
                                            onChange={ handleNotificationChange }
                                        />
                                    }
                                    label="Push"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="sms.newEvent"
                                            checked={ user.notifications?.sms?.newEvent || false }
                                            onChange={ handleNotificationChange }
                                            disabled={ !user.phoneVerified || !user.smsConsent }
                                        />
                                    }
                                    label="SMS"
                                />
                            </CardActions>
                        </CardContent>
                    </Card>

                    {/* MARK: Upcoming Shift */ }
                    <Card variant='outlined'>
                        <CardHeader
                            title="Upcoming Shift"
                            subheader="Reminders sent 24 hours before scheduled shift begins."
                        />

                        <CardContent>
                            <CardActions sx={ { p: 0 } }>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={ user.notifications?.email?.upcomingShift || false }
                                            onChange={ handleNotificationChange }
                                            name="email.upcomingShift"
                                            disabled={ !user.email }
                                        />
                                    }
                                    label="Email"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={ user.notifications?.push?.upcomingShift || false }
                                            onChange={ handleNotificationChange }
                                            name="push.upcomingShift"
                                        />
                                    }
                                    label="Push"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="sms.upcomingShift"
                                            checked={ user.notifications?.sms?.upcomingShift || false }
                                            onChange={ handleNotificationChange }
                                            disabled={ !user.phoneVerified || !user.smsConsent }
                                        />
                                    }
                                    label="SMS"
                                />
                            </CardActions>
                        </CardContent>
                    </Card>

                    {/* MARK: Shift Start */ }
                    <Card variant='outlined'>
                        <CardHeader
                            title="Shift Start"
                            subheader="Reminders sent 1 hour before your shift starts."
                        />

                        <CardContent>
                            <CardActions sx={ { p: 0 } }>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={ user.notifications?.email?.shiftStart || false }
                                            onChange={ handleNotificationChange }
                                            name="email.shiftStart"
                                            disabled={ !user.email }
                                        />
                                    }
                                    label="Email"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={ user.notifications?.push?.shiftStart || false }
                                            onChange={ handleNotificationChange }
                                            name="push.shiftStart"
                                        />
                                    }
                                    label="Push"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="sms.shiftStart"
                                            checked={ user.notifications?.sms?.shiftStart || false }
                                            onChange={ handleNotificationChange }
                                            disabled={ !user.phoneVerified || !user.smsConsent }
                                        />
                                    }
                                    label="SMS"
                                />
                            </CardActions>
                        </CardContent>
                    </Card>

                    {/* MARK: Missed Shift */ }
                    <Card variant='outlined'>
                        <CardHeader
                            title="Missed Shift"
                            subheader="Sent if you haven't clocked in or out of your assigned shift."
                        />

                        <CardContent>
                            <CardActions sx={ { p: 0 } }>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={ user.notifications?.email?.missedShift || false }
                                            onChange={ handleNotificationChange }
                                            name="email.missedShift"
                                            disabled={ !user.email }
                                        />
                                    }
                                    label="Email"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={ user.notifications?.push?.missedShift || false }
                                            onChange={ handleNotificationChange }
                                            name="push.missedShift"
                                        />
                                    }
                                    label="Push"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="sms.missedShift"
                                            checked={ user.notifications?.sms?.missedShift || false }
                                            onChange={ handleNotificationChange }
                                            disabled={ !user.phoneVerified || !user.smsConsent }
                                        />
                                    }
                                    label="SMS"
                                />
                            </CardActions>
                        </CardContent>
                    </Card>

                    {/* MARK: Available Shift */ }
                    <Card variant='outlined'>
                        <CardHeader
                            title="Available Shift"
                            subheader="Sent when another patroller cancels a shift within 72 hours of the shift start."
                        />
                        <CardContent>
                            <CardActions sx={ { p: 0 } }>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="email.shiftAvailable"
                                            checked={ user.notifications?.email?.shiftAvailable || false }
                                            onChange={ handleNotificationChange }
                                            disabled={ !user.email }
                                        />
                                    }
                                    label="Email"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={ user.notifications?.push?.shiftAvailable || false }
                                            onChange={ handleNotificationChange }
                                            name="push.shiftAvailable"
                                        />
                                    }
                                    label="Push"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="sms.shiftAvailable"
                                            checked={ user.notifications?.sms?.shiftAvailable || false }
                                            onChange={ handleNotificationChange }
                                            disabled={ !user.phoneVerified || !user.smsConsent }
                                        />
                                    }
                                    label="SMS"
                                />
                            </CardActions>
                        </CardContent>
                    </Card>

                    <Divider sx={ { my: 2 } } />

                    {/* MARK: Cancelled Shift */ }
                    <Card variant='outlined' sx={ { border: '1px solid', borderColor: 'info.light' } }>
                        <CardHeader
                            title={ <span>Cancelled Shift <span className={ styles.requiredBadge }>REQUIRED</span></span> }
                            subheader="Immediate notification when a shift is cancelled (e.g. hill closed, schedule changes)."
                            slotProps={ { title: { color: 'info.light' } } }
                        />

                        <CardContent>
                            <Alert variant='filled' severity="info" sx={ { mb: 2 } }>
                                This is a required notification. At least one notification method must be selected.
                            </Alert>

                            <CardActions sx={ { p: 0 } }>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={ user.notifications?.email?.shiftCancelled || false }
                                            onChange={ handleCancelledShiftChange }
                                            name="email.shiftCancelled"
                                            disabled={ !user.email }
                                            color="info"
                                        />
                                    }
                                    label="Email"
                                    sx={ { color: user.notifications?.email?.shiftCancelled ? 'info.light' : 'text.disabled' } }
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={ user.notifications?.push?.shiftCancelled || false }
                                            onChange={ handleNotificationChange }
                                            name="push.shiftCancelled"
                                        />
                                    }
                                    label="Push"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="sms.shiftCancelled"
                                            checked={ user.notifications?.sms?.shiftCancelled || false }
                                            onChange={ handleCancelledShiftChange }
                                            disabled={ !user.phoneVerified || !user.smsConsent }
                                        />
                                    }
                                    label="SMS"
                                    sx={ { color: user.notifications?.sms?.shiftCancelled ? 'info.light' : 'text.disabled' } }
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="voice.shiftCancelled"
                                            checked={ user.notifications?.voice?.shiftCancelled || false }
                                            onChange={ handleCancelledShiftChange }
                                            disabled={ !user.phoneVerified || !user.smsConsent }
                                        />
                                    }
                                    label="Voice"
                                    sx={ { color: user.notifications?.voice?.shiftCancelled ? 'info.light' : 'text.disabled' } }
                                />
                            </CardActions>
                        </CardContent>
                    </Card>

                    {/* MARK: Urgent Shift Request */ }
                    <Card variant='outlined' sx={ { border: '1px solid', borderColor: 'warning.light' } }>
                        <CardHeader
                            title={ <span>Urgent Shift Request <span className={ styles.warningBadge }>NON-EMERGENCY</span></span> }
                            subheader="Immediate notification for urgent shift requests (short-staffed, large groups, etc.)."
                            slotProps={ { title: { color: 'warning.light' } } }
                        />

                        <CardContent>
                            <div className={ styles.notificationMethods }>
                                <CardActions sx={ { p: 0 } }>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="email.urgentRequest"
                                                checked={ user.notifications?.email?.urgentRequest || false }
                                                onChange={ handleNotificationChange }
                                                disabled={ !user.email }
                                                color='warning'
                                            />
                                        }
                                        label="Email"
                                        sx={ { color: user.notifications?.email?.urgentRequest ? 'warning.light' : 'text.disabled' } }
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={ user.notifications?.push?.urgentRequest || false }
                                                onChange={ handleNotificationChange }
                                                name="push.urgentRequest"
                                            />
                                        }
                                        label="Push"
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="sms.urgentRequest"
                                                checked={ user.notifications?.sms?.urgentRequest || false }
                                                onChange={ handleNotificationChange }
                                                disabled={ !user.phoneVerified || !user.smsConsent }
                                                color='warning'
                                            />
                                        }
                                        label="SMS"
                                        sx={ { color: user.notifications?.sms?.urgentRequest ? 'warning.light' : 'text.disabled' } }
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="voice.urgentRequest"
                                                checked={ user.notifications?.voice?.urgentRequest || false }
                                                onChange={ handleNotificationChange }
                                                disabled={ !user.phoneVerified }
                                                color='warning'
                                            />
                                        }
                                        label="Voice"
                                        sx={ { color: user.notifications?.voice?.urgentRequest ? 'warning.light' : 'text.disabled' } }
                                    />
                                </CardActions>
                            </div>
                        </CardContent>
                    </Card>

                    {/* MARK: Chair Lift Evacuation - Critical */ }
                    <Card variant='outlined' sx={ { border: '1px solid', borderColor: 'error.light' } }>
                        <CardHeader
                            title={ <span>Chair Lift Evac <span className={ styles.criticalBadge }>CRITICAL</span></span> }
                            subheader="By enabling this, you are committing to respond immediately when contacted."
                            slotProps={ { title: { color: 'error.light' }, subheader: { color: 'warning.light' } } }
                        />

                        <CardContent>
                            <CardActions sx={ { p: 0 } }>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="email.chairLiftEvac"
                                            checked={ user.notifications?.email?.chairLiftEvac || false }
                                            onChange={ handleNotificationChange }
                                            disabled={ !user.email }
                                            color="error"
                                        />
                                    }
                                    label="Email"
                                    sx={ { color: user.notifications?.email?.chairLiftEvac ? 'error.light' : 'text.disabled' } }
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={ user.notifications?.push?.chairLiftEvac || false }
                                            onChange={ handleNotificationChange }
                                            name="push.chairLiftEvac"
                                        />
                                    }
                                    label="Push"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="sms.chairLiftEvac"
                                            checked={ user.notifications?.sms?.chairLiftEvac || false }
                                            onChange={ handleNotificationChange }
                                            disabled={ !user.phoneVerified || !user.smsConsent }
                                            color="error"
                                        />
                                    }
                                    label="SMS"
                                    sx={ { color: user.notifications?.sms?.chairLiftEvac ? 'error.light' : 'text.disabled' } }
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="voice.chairLiftEvac"
                                            checked={ user.notifications?.voice?.chairLiftEvac || false }
                                            onChange={ handleNotificationChange }
                                            disabled={ !user.phoneVerified }
                                            color="error"
                                        />
                                    }
                                    label="Voice"
                                    sx={ { color: user.notifications?.voice?.chairLiftEvac ? 'error.light' : 'text.disabled' } }
                                />
                            </CardActions>
                        </CardContent>
                    </Card>
                </Stack>
            </AccordionDetails>
        </Accordion>
    )
};